import React from 'react';
import { OrderCardWithButtonProps, ShopeeOrderInfo } from '.';
import UIOptimisationMilestone1 from '@/assets/img/ui-optimisation-recommender/ui-optimisation-milestone-1.svg';
import UIOptimisationMilestone2 from '@/assets/img/ui-optimisation-recommender/ui-optimisation-milestone-2.svg';
import UIOptimisationMilestone3 from '@/assets/img/ui-optimisation-recommender/ui-optimisation-milestone-3.svg';
import UIOptimisationMilestone4 from '@/assets/img/ui-optimisation-recommender/ui-optimisation-milestone-4.svg';
import { getTrans, replaceParams } from '@/utils/instantLoaded/tools';
import {
  SHIP_BY_DATE,
  EXPECTED_RECEIVE_TIME,
  MSG_MILESTONE_GROUP_READY_FOR_COLLECTION,
  DELIVERY_TIME,
  CANCEL_TIME,
  MSG_MILESTONE_GROUP_DELIVERY_UNSUCCESSFUL,
  DELIVERING_BY,
  ESTIMATED_DELIVERY_ON_BY,
} from '@/utils/constants';
import dateLib from '@/utils/dateLib/moment';
import getHqUIOptimisationGroup, { HQ_UI_OPTIMISATION_GROUP } from '@/utils/experiment/getHqUIOptimisation';
import { COUNTRY } from '@/utils/locale';

interface GetOrderMilestoneProps {
  milestone?: ShopeeOrderInfo['milestone'];
  useUIOptimisationHotQuestion?: OrderCardWithButtonProps['useUIOptimisationHotQuestion'];
  order?: any;
};

const handleTime = (date?: number) => {
  if (!date) {
    return '';
  }

  const twDate = dateLib.format(date * 1000, 'MMM DD日') + '';
  // tw 去除前面的 0 和 空格
  return COUNTRY === 'tw' ? twDate.replace(/(\b0)(?=\d)/g, '').replace(' ', '') : dateLib.format(date * 1000, 'DD MMM');
};

const getOrderMilestone = (props: GetOrderMilestoneProps) => {
  const { milestone, useUIOptimisationHotQuestion, order } = props;
  const defaultImageProps = {
    style: {
      width: '1rem',
      height: '1rem',
      marginRight: '0.25rem',
    }
  };

  if (useUIOptimisationHotQuestion && order) {
    switch (milestone) {
      case OrderMilestone.PreparingToShip:
        if (!order?.ship_by_date) {
          return null;
        }
        return {
          text: `${getTrans(SHIP_BY_DATE) || 'Seller to ship by'}: ${handleTime(order?.ship_by_date)}`,
          icon: <img src={UIOptimisationMilestone1} className="order-milestone-icon" {...defaultImageProps} />,
        };
        break;

      case OrderMilestone.ProcessingForOverseasShipment:
      case OrderMilestone.InTransitToDestinationCountry:
      case OrderMilestone.ArrivedAtDestinationCountry:
        if (!order?.expected_receive_time) {
          return null;
        }
        return {
          text: `${getTrans(EXPECTED_RECEIVE_TIME) || 'Estimated Delivery on'}: ${handleTime(order?.expected_receive_time)}`,
          icon: <img src={UIOptimisationMilestone2} className="order-milestone-icon" {...defaultImageProps} />,
        };
        break;

      case OrderMilestone.InTransit:
      case OrderMilestone.OutForDelivery:
        if (!order?.expected_receive_time) {
          return null;
        }
        let InTransitAndOutForDeliveryText = `${getTrans(EXPECTED_RECEIVE_TIME) || 'Estimated Delivery on'}: ${handleTime(order?.expected_receive_time)}`;
        const three_pl_name = COUNTRY === 'tw' ? order?.delivered_by : order?.threepl_name;
        if (order?.threepl_is_handled === 1) {
          const hqUIOptimisationGroup = getHqUIOptimisationGroup();
          if (hqUIOptimisationGroup === HQ_UI_OPTIMISATION_GROUP.THREE_PL_NAME && three_pl_name) {
            InTransitAndOutForDeliveryText = `${getTrans(DELIVERING_BY) || 'Delivering by'} ${three_pl_name}`;
          } else if (hqUIOptimisationGroup === HQ_UI_OPTIMISATION_GROUP.EDD_THREE_PL_NAME && three_pl_name) {
            const translationText = getTrans(ESTIMATED_DELIVERY_ON_BY);
            InTransitAndOutForDeliveryText = translationText ? replaceParams(translationText, {
              expected_receive_time: handleTime(order?.expected_receive_time),
              three_pl_name,
            }).toString() : `Estimated Delivery on ${handleTime(order?.expected_receive_time)} by ${three_pl_name}`;
          }
        }
        return {
          text: InTransitAndOutForDeliveryText,
          icon: <img src={UIOptimisationMilestone2} className="order-milestone-icon" {...defaultImageProps} />,
        };
        break;

      case OrderMilestone.ReadyForCollection:
        return {
          text: getTrans(MSG_MILESTONE_GROUP_READY_FOR_COLLECTION) || 'Ready for collection',
          icon: <img src={UIOptimisationMilestone3} className="order-milestone-icon" {...defaultImageProps} />,
        };
        break;

      case OrderMilestone.Delivered:
        if (!order?.receive_time) {
          return null;
        }
        return {
          text: `${getTrans(DELIVERY_TIME) || 'Delivered on'}: ${handleTime(order?.receive_time)}`,
          icon: <img src={UIOptimisationMilestone3} className="order-milestone-icon" {...defaultImageProps} />,
        };
        break;

      case OrderMilestone.Cancelled:
        if (!order?.cancel_time) {
          return null;
        }
        return {
          text: `${getTrans(CANCEL_TIME) || 'Cancelled on'}: ${handleTime(order?.cancel_time)}`,
          icon: <img src={UIOptimisationMilestone4} className="order-milestone-icon" {...defaultImageProps} />,
        };
        break;

      case OrderMilestone.DeliveryUnsuccessful:
        return {
          text: getTrans(MSG_MILESTONE_GROUP_DELIVERY_UNSUCCESSFUL) || 'Delivery Unsuccessful',
          icon: <img src={UIOptimisationMilestone4} className="order-milestone-icon" {...defaultImageProps} />,
        };
        break;
    }
  }
  return null;
};

enum OrderMilestone {
  PreparingToShip = 1,
  ProcessingForOverseasShipment,
  InTransitToDestinationCountry,
  ArrivedAtDestinationCountry,
  InTransit,
  OutForDelivery,
  ReadyForCollection,
  Delivered,
  Cancelled,
  DeliveryUnsuccessful,
};

export default getOrderMilestone;
